/* Quick view.

   A short version of the product page rather than a bare confirm dialog: the
   full image set with a counter and dots, the title, the price with whatever
   saving applies, the options, a quantity, then one add.

   It shows every product image, not only the one attached to the current
   variant, because most variants here share a single photo and a gallery that
   collapsed to one frame told you less than the card you tapped. Choosing an
   option jumps the gallery to that variant's image when it has its own.

   Single-variant products still add in one tap: nothing to choose. */
(() => {
  const ID = 'qv-sheet';
  const cache = {};
  let product = null;
  let selected = null;
  let qty = 1;

  const money = (c) => '$' + (c / 100).toFixed(2);
  const sized = (src, w) => src ? src.replace(/(\.[a-z]+)(\?|$)/i, '_' + w + 'x$1$2') : '';

  function build() {
    let el = document.getElementById(ID);
    if (el) return el;
    el = document.createElement('div');
    el.id = ID;
    el.className = 'qv';
    el.hidden = true;
    el.innerHTML = `
      <div class="qv__scrim" data-qv-close></div>
      <div class="qv__sheet" role="dialog" aria-modal="true" aria-labelledby="qv-title">
        <span class="qv__grab" aria-hidden="true"></span>
        <button type="button" class="qv__close" data-qv-close aria-label="Close">&times;</button>

        <div class="qv__gallery">
          <div class="qv__strip" data-qv-strip></div>
          <div class="qv__counter" data-qv-counter aria-live="polite"></div>
          <div class="qv__dots" data-qv-dots></div>
        </div>

        <div class="qv__body">
          <div class="qv__head">
            <h2 class="qv__title" id="qv-title" data-qv-title></h2>
            <p class="qv__price">
              <span data-qv-price></span>
              <s data-qv-compare hidden></s>
              <em class="qv__save" data-qv-save hidden></em>
            </p>
          </div>
          <div class="qv__options" data-qv-options></div>
          <div class="qv__buy">
            <div class="qv__qty">
              <button type="button" data-qv-dec aria-label="Decrease">&minus;</button>
              <span data-qv-qty>1</span>
              <button type="button" data-qv-inc aria-label="Increase">+</button>
            </div>
            <button type="button" class="qv__add" data-qv-add>Add to bag</button>
          </div>
          <a class="qv__full" data-qv-full href="#">View full details</a>
        </div>
      </div>`;
    document.body.appendChild(el);

    el.addEventListener('click', (e) => {
      if (e.target.closest('[data-qv-close]')) return close();
      if (e.target.closest('[data-qv-inc]')) { qty += 1; syncQty(); }
      if (e.target.closest('[data-qv-dec]')) { qty = Math.max(1, qty - 1); syncQty(); }
      if (e.target.closest('[data-qv-add]')) add();
    });

    const strip = el.querySelector('[data-qv-strip]');
    strip.addEventListener('scroll', () => {
      const i = Math.round(strip.scrollLeft / strip.clientWidth);
      markSlide(i);
    }, { passive: true });
    return el;
  }

  function syncQty() {
    build().querySelector('[data-qv-qty]').textContent = qty;
  }

  function markSlide(i) {
    const el = build();
    const total = el.querySelectorAll('.qv__slide').length;
    if (!total) return;
    const n = Math.min(Math.max(i, 0), total - 1);
    el.querySelector('[data-qv-counter]').textContent = (n + 1) + ' / ' + total;
    el.querySelectorAll('[data-qv-dots] button').forEach((d, k) =>
      d.classList.toggle('is-active', k === n));
  }

  function goToSlide(i) {
    const strip = build().querySelector('[data-qv-strip]');
    strip.scrollTo({ left: i * strip.clientWidth, behavior: 'smooth' });
  }

  function renderGallery() {
    const el = build();
    const strip = el.querySelector('[data-qv-strip]');
    const dots = el.querySelector('[data-qv-dots]');
    const imgs = (product.images && product.images.length ? product.images : [product.featured_image])
      .filter(Boolean).slice(0, 8);

    strip.innerHTML = imgs.map(src =>
      '<div class="qv__slide"><img src="' + sized(src, 700) + '" alt="' + product.title.replace(/"/g, '') + '" loading="lazy"></div>'
    ).join('');
    dots.innerHTML = imgs.map((_, i) =>
      '<button type="button" aria-label="Image ' + (i + 1) + '"></button>').join('');
    dots.querySelectorAll('button').forEach((d, i) => d.addEventListener('click', () => goToSlide(i)));
    el.querySelector('[data-qv-counter]').hidden = imgs.length < 2;
    dots.hidden = imgs.length < 2;
    markSlide(0);
    return imgs;
  }

  function paint() {
    const el = build();
    const v = selected;
    el.querySelector('[data-qv-title]').textContent = product.title;
    el.querySelector('[data-qv-price]').textContent = money(v.price);
    const cmp = el.querySelector('[data-qv-compare]');
    const save = el.querySelector('[data-qv-save]');
    const has = v.compare_at_price && v.compare_at_price > v.price;
    cmp.textContent = has ? money(v.compare_at_price) : '';
    cmp.hidden = !has;
    if (has) {
      save.textContent = 'Save ' + Math.round((1 - v.price / v.compare_at_price) * 100) + '%';
      save.hidden = false;
    } else save.hidden = true;

    el.querySelector('[data-qv-full]').href = '/products/' + product.handle;
    const add = el.querySelector('[data-qv-add]');
    add.disabled = !v.available;
    add.textContent = v.available ? 'Add to bag' : 'Sold out';

    // Jump the gallery to this variant's own photo when it has one.
    const vs = v.featured_image && v.featured_image.src;
    if (vs) {
      const idx = (product.images || []).findIndex(s => s.split('?')[0] === vs.split('?')[0]);
      if (idx > -1) goToSlide(idx);
    }
  }

  function renderOptions() {
    const host = build().querySelector('[data-qv-options]');
    host.innerHTML = '';
    if (product.variants.length < 2) return;

    product.options.forEach((opt, idx) => {
      // /products/x.js returns option objects, not strings, so the raw value
      // printed as [object Object].
      const name = (opt && opt.name) ? opt.name : opt;
      const wrap = document.createElement('div');
      wrap.className = 'qv__opt';
      const head = document.createElement('div');
      head.className = 'qv__opt-head';
      head.innerHTML = '<span class="qv__opt-label">' + name + '</span>' +
        '<span class="qv__opt-value">' + selected.options[idx] + '</span>';
      wrap.appendChild(head);

      const row = document.createElement('div');
      row.className = 'qv__opt-row';
      const seen = [];
      product.variants.forEach(v => {
        const val = v.options[idx];
        if (seen.includes(val)) return;
        seen.push(val);
        const ok = product.variants.some(x => x.options[idx] === val && x.available);
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'qv__chip';
        b.textContent = val;
        b.disabled = !ok;
        if (selected.options[idx] === val) b.classList.add('is-on');
        b.addEventListener('click', () => {
          const want = selected.options.slice();
          want[idx] = val;
          selected = product.variants.find(v2 => v2.options.every((o, i) => o === want[i]) && v2.available)
            || product.variants.find(v2 => v2.options[idx] === val && v2.available)
            || selected;
          renderOptions();
          paint();
        });
        row.appendChild(b);
      });
      wrap.appendChild(row);
      host.appendChild(wrap);
    });
  }

  function add() {
    const btn = build().querySelector('[data-qv-add]');
    btn.disabled = true;
    btn.textContent = 'Adding';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: selected.id, quantity: qty }] }),
    }).then(() => {
      close();
      if (window.CartDrawer) window.CartDrawer.refresh().then(window.CartDrawer.open);
      else window.location.reload();
    }).catch(() => { btn.disabled = false; btn.textContent = 'Add to bag'; });
  }

  function open() {
    const el = build();
    el.hidden = false;
    requestAnimationFrame(() => el.classList.add('is-open'));
    document.documentElement.classList.add('qv-open');
  }
  function close() {
    const el = document.getElementById(ID);
    if (!el) return;
    el.classList.remove('is-open');
    document.documentElement.classList.remove('qv-open');
    setTimeout(() => { el.hidden = true; }, 280);
  }

  const load = (h) => cache[h] ? Promise.resolve(cache[h])
    : fetch('/products/' + h + '.js').then(r => r.json()).then(p => (cache[h] = p));

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    const handle = btn.dataset.quickHandle;
    if (!handle || Number(btn.dataset.quickVariants || 1) <= 1) return;
    e.preventDefault();
    e.stopPropagation();
    load(handle).then(p => {
      product = p;
      selected = p.variants.find(v => v.available) || p.variants[0];
      qty = 1;
      syncQty();
      renderGallery();
      renderOptions();
      paint();
      open();
    });
  }, true);

  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
})();
